import { Core } from "@faire/design-tokens";
import { trackProductPageReviewSectionToggleClick } from "@faire/web-api/events/productPage/click/reviewSectionToggle";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Chevron } from "@faire/web/slate/icons/Chevron";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useState } from "@faire/web/ui/hooks/useState";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";

import { RatingDisplay } from "@faire/retailer/components/RatingStars/RatingDisplay";
import { IReviewSummary } from "@faire/retailer/lib/buildReviewSummary";

import { ReviewsSection } from "../__internal__/Product/ReviewsSection";

type Props = ReviewsAccordion.IProps;

export namespace ReviewsAccordion {
  export interface IProps {
    productReviewSummary?: IReviewSummary;
    brandReviewSummary?: IReviewSummary;
    brand: IProductPageResponse.IPdpBrand;
    product: IProductPageResponse.IPdpProduct;
  }
}

const ReviewsAccordionBase: React.FunctionComponent<Props> = ({
  productReviewSummary,
  brandReviewSummary,
  brand,
  product,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  if (!brand || !product || !brandReviewSummary) {
    return null;
  }
  const title = (
    <Row align="center">
      <Typography variant="paragraphSansMedium">
        {strictLocalize({
          defaultMessage: "Reviews",
          description: {
            text: "Section title. Text of button to expand the brand and product reviews section",
            img: "https://cdn.faire.com/fastly/0fc3cfa44aad1791f8523867a8083be8590389c7c27c5193a7181d674601a44f.png",
          },
        })}
      </Typography>
      <Spacer size={4} />
      <RatingDisplay rating={brandReviewSummary.overallRating} size="compact" />
      <Spacer size={4} />
      <Typography variant="paragraphSansRegular">
        {brandReviewSummary.overallRatingText} (
        {brandReviewSummary.numberOfReviews})
      </Typography>
    </Row>
  );

  const toggleAccordion = () => {
    trackProductPageReviewSectionToggleClick(
      isAccordionOpen ? "close" : "open",
      product.token ?? ""
    );
    setIsAccordionOpen(!isAccordionOpen);
  };

  return (
    <>
      <StyledAccordionContainer>
        <Column style={{ width: "100%" }}>
          <Row
            onClick={toggleAccordion}
            justify="space-between"
            style={{ width: "100%" }}
          >
            {title}
            <ChevronRow align="flex-start">
              <AnimatedChevron $isAccordionOpen={isAccordionOpen}>
                <Chevron
                  data-test-id="accordion-chevron"
                  direction="right"
                  fontSize="16px"
                  color={Core.icon.default}
                />
              </AnimatedChevron>
            </ChevronRow>
          </Row>
          <AccordionComponentSpan>
            {isAccordionOpen ? (
              <>
                <Spacer size="2x" />
                <ReviewsSection
                  brand={brand}
                  product={product}
                  productReviewSummary={productReviewSummary}
                  brandReviewSummary={brandReviewSummary}
                />
              </>
            ) : null}
          </AccordionComponentSpan>
        </Column>
      </StyledAccordionContainer>
      <HorizontalLine />
    </>
  );
};

const StyledAccordionContainer = styled(Row)`
  width: 100%;
  padding: ${getSpacing("3x", 0)};
  cursor: pointer;
`;

const ACCORDION_TRANSITION_TIMING = `
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;

const ChevronRow = styled(Row)`
  margin-left: auto;
  margin-right: 0;
`;

const AccordionComponentSpan = styled(Span)`
  text-align: start;
  word-break: break-word;
  white-space: pre-wrap;
  width: 100%;
  transition-property: max-height;
  ${ACCORDION_TRANSITION_TIMING}
`;

const AnimatedChevron = styled.div<{ $isAccordionOpen: boolean }>`
  transform: rotate(
    ${({ $isAccordionOpen }) =>
      css`
        ${$isAccordionOpen ? -90 : 90}deg
      `}
  );
  transition-property: transform;
  ${ACCORDION_TRANSITION_TIMING}
`;

export const ReviewsAccordion = observer(ReviewsAccordionBase);
